import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff, RefreshCw, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { AccessEntryFields, ACCESS_LOCATIONS, GATE_GROUP, accessSlug } from './AccessEntryFields';
import FloorPlanUpload from './FloorPlanUpload';
import { AmenityCsvImport } from './AmenityCsvImport';
import { isResidentPropertyType } from '../lib/roleTerm';

const API = process.env.REACT_APP_BACKEND_URL;
const loadGroups = (property) => {
  const groups = { [GATE_GROUP]: property.gate_locations || (property.gate_entry_codes || (property.gate_code ? [property.gate_code] : [])).map((code, i) => ({ location_name: ACCESS_LOCATIONS[i] || 'Main', entry_code: code })) };
  for (const item of property.amenities || []) {
    const row = typeof item === 'string' ? { name: item } : item;
    if (!row?.name) continue;
    const normalized = isResidentPropertyType(property.property_type) && row.name.trim().toLowerCase() === 'gym' ? 'Study Room' : row.name;
    const name = Object.keys(groups).find((n) => n.toLowerCase() === normalized.toLowerCase()) || normalized;
    groups[name] ||= [];
    if (!groups[name].some((r) => r.location_name === (row.location_name || 'Main')))
      groups[name].push({ ...row, location_name: row.location_name || 'Main', entry_code: row.entry_code || '' });
  }
  return groups;
};

export default function PropertyAccessEntryMirror({ propertyId, readOnly = false }) {
  const [property, setProperty] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [groups, setGroups] = useState({});
  const saved = useRef({});
  const revision = useRef(0);
  const saving = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reveal, setReveal] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditError, setAuditError] = useState('');
  const [reload, setReload] = useState(0);
  const [newAmenity, setNewAmenity] = useState(null);

  const loadAudit = useCallback(async () => {
    if (readOnly) return;
    try {
      const res = await fetch(`${API}/api/properties/${propertyId}/access-codes/audit`, { credentials: 'include' });
      if (!res.ok) throw new Error('Audit trail unavailable for this account.');
      const data = await res.json(); setAudit(data.events || []); setAuditError('');
    } catch (e) { setAuditError(e.message); }
  }, [propertyId, readOnly]);

  useEffect(() => {
    const controller = new AbortController();
    setProperty(null); setGroups({}); setError(''); setReveal(false); setAudit([]); setNewAmenity(null);
    if (!propertyId) return;
    (async () => {
      try {
        const opts = { credentials: 'include', signal: controller.signal };
        const [res, types] = await Promise.all([fetch(`${API}/api/properties/${propertyId}`, opts), fetch(`${API}/api/commercial/property-types`, opts)]);
        if (!res.ok || !types.ok) throw new Error('Unable to load access codes. Please retry.');
        const prop = await res.json(), meta = await types.json();
        if (controller.signal.aborted) return;
        const key = (prop.property_type || 'residential').toLowerCase().replace('multifamily', 'residential');
        const arr = Array.isArray(meta) ? meta : meta.types || meta.property_types || [];
        setCatalog((arr.find((type) => type.key === key)?.amenities || []).filter(name => name.toLowerCase() !== 'other'));
        const initial = loadGroups(prop);
        saved.current = initial; revision.current = prop.access_codes_revision || 0;
        setGroups(initial); setProperty(prop); loadAudit();
      } catch (e) { if (e.name !== 'AbortError') setError(e.message); }
    })();
    return () => controller.abort();
  }, [propertyId, reload, loadAudit]);

  const names = useMemo(() => {
    const seen = new Set();
    return [GATE_GROUP, ...catalog, ...Object.keys(groups)].filter((name) => {
      if (seen.has(name.toLowerCase())) return false;
      seen.add(name.toLowerCase()); return true;
    }).map((name) => Object.keys(groups).find((n) => n.toLowerCase() === name.toLowerCase()) || name);
  }, [catalog, groups]);

  const changeRow = (name, i, updates) => setGroups((s) => ({ ...s, [name]: s[name].map((row, idx) => idx === i ? { ...row, ...updates } : row) }));
  const validateName = (value, oldName) => {
    const name = value.trim().replace(/\s+/g, ' ');
    if (!name || name.length > 120 || name.toLowerCase() === 'other') throw new Error('Enter a specific amenity name (1–120 characters).');
    if (names.some(n => n !== oldName && accessSlug(n) === accessSlug(name))) throw new Error('An amenity with that name already exists.');
    return name;
  };
  const addAmenity = event => {
    event.preventDefault();
    try { const name = validateName(newAmenity); setGroups(s => ({ ...s, [name]: [{ location_name: 'Main', entry_code: '', editing: true }] })); setNewAmenity(null); setError(''); }
    catch(e) { setError(e.message); }
  };
  const renameAmenity = async (oldName, value) => {
    if (readOnly || saving.current) return false;
    try {
      const name = validateName(value, oldName);
      if (name === oldName) return true;
      if (!(saved.current[oldName] || []).length) {
        setGroups(s => { const next = { ...s, [name]: s[oldName] }; delete next[oldName]; return next; }); return true;
      }
      saving.current = true; setBusy(true);
      const next = { ...saved.current, [name]: saved.current[oldName] }; delete next[oldName];
      const payload = { expected_revision: revision.current, amenities: Object.entries(next).filter(([n]) => n !== GATE_GROUP).flatMap(([n, rows]) => rows.map(({editing, ...r}) => ({ ...r, name: n }))) };
      const res = await fetch(`${API}/api/properties/${propertyId}/access-codes`, { method:'PUT', credentials:'include', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) });
      const data = await res.json(); if (!res.ok) throw new Error(data.detail || 'Unable to rename amenity');
      revision.current = data.patch.access_codes_revision; saved.current = next;
      setGroups(s => { const copy = { ...s, [name]: s[oldName] }; delete copy[oldName]; return copy; });
      setError(''); toast.success('Amenity renamed'); loadAudit(); return true;
    } catch(e) { setError(e.message); return false; }
    finally { saving.current = false; setBusy(false); }
  };
  const save = async (name, i, remove = false) => {
    if (saving.current || readOnly) return;
    const row = groups[name][i];
    // Deleting an unsaved row must not persist any other drafts.
    const original = (saved.current[name] || []).find((r) => r.location_name === row.location_name);
    if (remove && !original) { setGroups((s) => ({ ...s, [name]: s[name].filter((r) => r.location_name !== row.location_name) })); return; }
    if (remove && !window.confirm(`Delete ${name} · ${row.location_name}?`)) return;
    const nextRows = (saved.current[name] || []).filter((r) => r.location_name !== row.location_name);
    if (!remove) nextRows.push({ ...row, entry_code: row.entry_code.trim(), editing: false });
    const next = { ...saved.current, [name]: nextRows };
    const payload = { expected_revision: revision.current, gate_locations: next[GATE_GROUP] || [], amenities: Object.entries(next).filter(([n]) => n !== GATE_GROUP).flatMap(([n, rows]) => rows.map(({ editing, ...r }) => ({ ...r, name: n }))) };
    saving.current = true; setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/properties/${propertyId}/access-codes`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Unable to save access code.');
      revision.current = data.patch.access_codes_revision; saved.current = next;
      setGroups((s) => ({ ...s, [name]: remove ? s[name].filter((r) => r.location_name !== row.location_name) : s[name].map((r) => r.location_name === row.location_name ? { ...r, entry_code: row.entry_code.trim(), editing: false } : r) }));
      toast.success(remove ? 'Access code deleted.' : 'Access code saved.'); loadAudit();
    } catch (e) { setError(e.message); toast.error(e.message); }
    finally { saving.current = false; setBusy(false); }
  };

  return <section className="min-w-0 border-t-2 border-[#C9A962] bg-zinc-950 text-white p-3 sm:p-5 space-y-5" data-testid="property-access-entry-mirror">
    <header className="flex flex-wrap justify-between items-center gap-3">
      <h2 className="text-base md:text-lg font-bold text-[#FFD60A]" data-testid="ae-mirror-title">Access &amp; Entry {readOnly && '· Read only'}</h2>
      <div className="flex gap-2">
        {!readOnly && <button type="button" disabled={busy || !property} onClick={() => setNewAmenity('')} title="Add amenity" aria-label="Add amenity" className="p-2 bg-[#FFD60A] text-black rounded hover:bg-yellow-400 transition-colors disabled:opacity-40" data-testid="ae-mirror-add-amenity"><Plus size={16}/></button>}
        <button type="button" onClick={() => setReveal((r) => !r)} className="p-2 border border-zinc-600 rounded hover:bg-zinc-800 transition-colors" title={reveal ? 'Hide codes' : 'Reveal codes'} aria-label={reveal ? 'Hide codes' : 'Reveal codes'} data-testid="ae-mirror-reveal">{reveal ? <EyeOff size={16} /> : <Eye size={16} />}</button>
        <button type="button" disabled={busy} onClick={() => { const dirty = Object.values(groups).some((rows) => rows.some((r) => r.editing)); if (!dirty || window.confirm('Discard unsaved changes and reload access codes?')) setReload((n) => n + 1); }} className="p-2 border border-zinc-600 rounded hover:bg-zinc-800 transition-colors" title="Reload access codes" aria-label="Reload access codes" data-testid="ae-mirror-reload"><RefreshCw size={16} /></button>
      </div>
    </header>
    {!readOnly && newAmenity !== null && <form onSubmit={addAmenity} className="flex flex-wrap items-end gap-2" data-testid="ae-mirror-add-amenity-form">
      <label className="flex-1 min-w-0 text-xs text-[#C9A962]">Amenity name<input autoFocus value={newAmenity} onChange={e => setNewAmenity(e.target.value)} maxLength={120} required className="block mt-1 w-full rounded bg-zinc-900 border border-zinc-600 text-white p-2 text-sm" data-testid="ae-mirror-new-amenity-name"/></label>
      <button type="submit" disabled={busy} className="p-2 rounded bg-[#FFD60A] text-black" title="Add named amenity" aria-label="Add named amenity" data-testid="ae-mirror-confirm-amenity"><Check size={18}/></button>
      <button type="button" onClick={() => setNewAmenity(null)} className="p-2 rounded border border-zinc-600" title="Cancel" aria-label="Cancel" data-testid="ae-mirror-cancel-amenity"><X size={18}/></button>
    </form>}
    {error && <p role="alert" className="text-sm text-red-300 break-words" data-testid="ae-mirror-error">{error}</p>}
    {!property && !error && <p role="status" data-testid="ae-mirror-loading">{propertyId ? 'Loading access codes…' : 'Select a property.'}</p>}
    {property&&!readOnly&&<AmenityCsvImport key={propertyId} propertyId={propertyId} disabled={busy||Object.values(groups).some(rows=>rows.some(row=>row.editing))} onImported={()=>setReload(n=>n+1)}/>}
    {property && <AccessEntryFields names={names} groups={groups} readOnly={readOnly} reveal={reveal} busy={busy}
      onRename={renameAmenity} editableNames={names.filter(n => n !== GATE_GROUP && !catalog.some(c => c.toLowerCase() === n.toLowerCase()))}
      onAdd={(name, location) => setGroups((s) => ({ ...s, [name]: [...(s[name] || []), { location_name: location, entry_code: '', editing: true }] }))}
      onChange={(name, i, entry_code) => changeRow(name, i, { entry_code })} onEdit={(name, i) => changeRow(name, i, { editing: true })}
      onSave={save} onDelete={(name, i) => save(name, i, true)} />}
    {property && <FloorPlanUpload propertyId={propertyId} floorPlanUrl={property.floor_plan_url} readOnly={readOnly} />}
    {!readOnly && <section className="border-t border-zinc-700 pt-4" data-testid="ae-mirror-audit-block">
      <h3 className="text-sm font-bold text-[#C9A962] mb-3">Rotation Audit Trail</h3>
      {auditError ? <p role="status" data-testid="ae-mirror-audit-error" className="text-xs text-zinc-400">{auditError}</p> : audit.length === 0 ? <p data-testid="ae-mirror-audit-empty" className="text-xs text-zinc-400">No rotations on file.</p> : <div className="space-y-2 max-h-64 overflow-y-auto">
        {audit.map((ev, i) => <div key={ev.audit_id} className="flex flex-wrap gap-x-4 gap-y-1 border-b border-zinc-800 py-2 text-xs break-words" data-testid={`ae-mirror-audit-row-${i}`}>
          <span className="text-[#FFD60A]">{ev.field} · {ev.action}</span><span>{ev.old_masked} → {ev.new_masked}</span>
          <span className="text-zinc-400">{ev.edited_by_name} · {(ev.edited_at || '').slice(0, 16).replace('T', ' ')}</span>
        </div>)}
      </div>}
    </section>}
  </section>;
}